import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchDashboard } from '@/api/dashboard';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { HorizontalBars } from '@/components/charts/Charts';
import {
  BagIcon,
  CheckmarkIcon,
  AlertIcon,
  ChevronIcon,
  LightningIcon,
  RoiBarsIcon,
  RoiWalletIcon,
  RoiExchangeIcon,
  RoiBankIcon,
  RoiClockIcon,
} from '@/components/icons';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { lastNDaysRange, rangeDayCount } from '@/lib/dateRange';
import type { DateRange } from '@/lib/dateRange';
import { exportDashboardCsv } from '@/lib/csv';
import {
  formatCurrency,
  formatNumber,
  formatPercentDelta,
  formatRelativeTime,
  humanizeEnum,
  toNumber,
} from '@/lib/format';
import { cn } from '@/lib/cn';
import { toast } from '@/store/useToastStore';
import type { DashboardActivity, DashboardResponse } from '@/types';
import { paths } from '@/router/paths';

function percentText(value: number | null | undefined): string {
  return value == null ? '—' : `${toNumber(value).toFixed(1)}%`;
}

interface MetricDelta {
  text: string;
  /** true → good direction (green), false → bad (red). Controls colour. */
  isPositive: boolean;
  /** true → value rose (↑), false → value fell (↓). Controls the arrow. */
  up: boolean;
}

/**
 * Period-over-period change. `lowerIsBetter` flips the colour so a drop reads as
 * good (e.g. resolution time, refund outflow). Returns null when there's no
 * comparable previous value.
 */
function metricDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
  lowerIsBetter = false,
): MetricDelta | null {
  if (previous == null || toNumber(previous) === 0) {
    return null;
  }
  const change = ((toNumber(current) - toNumber(previous)) / toNumber(previous)) * 100;
  if (!Number.isFinite(change)) {
    return null;
  }
  const up = change >= 0;
  return { text: formatPercentDelta(change), isPositive: lowerIsBetter ? !up : up, up };
}

export function DashboardScreen() {
  const navigate = useNavigate();
  const [range, setRange] = useState<DateRange>(() => lastNDaysRange(30));
  const days = rangeDayCount(range);
  const { data, errorMessage, isLoading, reload } = useAsyncData<DashboardResponse | null>(
    (signal) => fetchDashboard(range, signal),
    [range.from, range.to],
  );

  function handleExport() {
    if (!data) {
      return;
    }
    exportDashboardCsv(data, days);
    toast.success('Dashboard exported');
  }

  const kpi = data?.kpi;
  const tiles = data?.actionTiles;
  const autopilot = data?.autopilot;
  const byReason = data?.byReason ?? [];
  const topRules = autopilot?.topRules ?? [];
  const recentActivity = data?.recentActivity ?? [];

  const prevReturns = toNumber(kpi?.prevTotalReturns);

  const reasonBars = byReason.map((reason) => ({
    label: reason.label,
    value: reason.count,
    caption: `${formatNumber(reason.count)} · ${percentText(reason.pct)}`,
  }));

  return (
    <div className="animate-fade-in pb-10">
      <PageHeader
        title="Dashboard"
        description="Your returns operation at a glance."
        actions={
          <>
            <DateRangePicker value={range} onChange={setRange} />
            <Button variant="secondary" size="sm" onClick={handleExport} disabled={!data || isLoading}>
              Export
            </Button>
          </>
        }
      />

      {isLoading ? (
        <LoadingState message="Loading dashboard…" />
      ) : errorMessage ? (
        <ErrorState message={errorMessage} onRetry={reload} />
      ) : (
        <>
          {/* Headline KPIs */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MetricCard
              label="Return requests"
              value={formatNumber(kpi?.totalReturns)}
              hint={prevReturns > 0 ? `vs ${formatNumber(prevReturns)} prev period` : `over the last ${days} days`}
              delta={metricDelta(kpi?.totalReturns, kpi?.prevTotalReturns,true)}
             // spark={spark}
              accent="#6366f1"
              tone="brand"
              icon={<BagIcon />}
            />
            <MetricCard
              label="Return rate"
              value={percentText(kpi?.returnRate)}
              hint="of all delivered orders"
              delta={metricDelta(kpi?.returnRate, kpi?.prevReturnRate, true)}
              tone="info"
              icon={<RoiBarsIcon />}
            />
            <MetricCard
              label="Exchange conversion"
              value={percentText(kpi?.exchangeConversion)}
              hint="returns turned into exchanges"
              delta={metricDelta(kpi?.exchangeConversion, kpi?.prevExchangeConversion)}
              tone="warning"
              icon={<RoiExchangeIcon />}
            />
            <MetricCard
              label="Store credit adoption"
              value={percentText(kpi?.storeCreditAdoption)}
              hint="of refunds taken as credit"
              delta={metricDelta(kpi?.storeCreditAdoption, kpi?.prevStoreCreditAdoption)}
              tone="success"
              icon={<RoiWalletIcon />}
            />
            <MetricCard
              label="Refunds issued"
              value={formatCurrency(kpi?.refundsIssued)}
              hint={`over the last ${days} days`}
              delta={metricDelta(kpi?.refundsIssued, kpi?.prevRefundsIssued, true)}
              tone="danger"
              icon={<RoiBankIcon />}
            />
            <MetricCard
              label="Revenue saved"
              value={formatCurrency(kpi?.revenueSaved)}
              hint="via exchanges & credit"
              delta={metricDelta(kpi?.revenueSaved, kpi?.prevRevenueSaved)}
              tone="success"
              icon={<span className="text-base font-semibold leading-none">₹</span>}
            />
            <MetricCard
              label="Automated rate"
              value={percentText(autopilot?.automatedRatePct)}
              hint="returns without human touch"
              tone="brand"
              icon={<LightningIcon />}
            />
            <MetricCard
              label="Avg. time to resolve"
              tone="info"
              icon={<RoiClockIcon />}
              value={
                      kpi?.avgResolveDays != null
                        ? toNumber(kpi.avgResolveDays) < 1
                          ? `${(toNumber(kpi.avgResolveDays) * 24).toFixed(1)}h`
                          : `${toNumber(kpi.avgResolveDays).toFixed(1)}d`
                        : '—'
              }
              hint="from submit to refund"
              delta={metricDelta(kpi?.avgResolveDays, kpi?.prevAvgResolveDays, true)}
            />
            {/* <MetricCard
              label="Upsell revenue"
              value={formatCurrency(kpi?.upsellRevenue)}
              hint="add-ons bought in the return flow"
              delta={metricDelta(kpi?.upsellRevenue, kpi?.prevUpsellRevenue)}
            /> */}
          </div>

          {/* Operations pipeline */}
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <PipelineStat label="Pending review" value={kpi?.pendingReview} onClick={() => navigate(paths.returns)} />
            <PipelineStat label="Active pickups" value={kpi?.activePickups} />
            <PipelineStat label="Received at WH" value={kpi?.receivedAtWarehouse} onClick={() => navigate(paths.qc)} />
            <PipelineStat label="Pending QC" value={kpi?.pendingQc} onClick={() => navigate(paths.qc)} />
            <PipelineStat label="Rejected" value={kpi?.rejected} tone="danger" />
          </div>

          {/* Attention + reasons */}
          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-subtle">
                Needs your attention
              </h2>
              <div className="flex flex-col gap-3">
                <AttentionTile
                  icon={<BagIcon />}
                  iconClass="bg-warning-soft text-warning"
                  label="Returns awaiting approval"
                  value={formatNumber(tiles?.awaitingApproval)}
                  onClick={() => navigate(paths.returns)}
                />
                <AttentionTile
                  icon={<CheckmarkIcon />}
                  iconClass="bg-info-soft text-info"
                  label="QC items in queue"
                  value={formatNumber(tiles?.qcQueue)}
                  onClick={() => navigate(paths.qc)}
                />
                <AttentionTile
                  icon={<AlertIcon />}
                  iconClass="bg-danger-soft text-danger"
                  label="Exceptions to resolve"
                  value={formatNumber(tiles?.exceptionsToResolve)}
                  onClick={() => navigate(paths.exceptions)}
                />
              </div>
            </section>

            <Card>
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-subtle">
                  Returns by reason · last {days} days
                </h2>
                <button
                  type="button"
                  onClick={() => navigate(paths.analytics)}
                  className="whitespace-nowrap text-sm font-medium text-brand hover:underline"
                >
                  View report ›
                </button>
              </div>
              <div className="mt-4">
                {reasonBars.length === 0 ? (
                  <p className="py-6 text-center text-sm text-ink-muted">No returns in this period.</p>
                ) : (
                  <HorizontalBars data={reasonBars} />
                )}
              </div>
            </Card>
          </div>

          {/* Live activity + autopilot */}
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card padded={false} className="overflow-hidden lg:col-span-2">
              <div className="flex items-center justify-between border-b border-line px-5 py-4">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-subtle">Live activity</h2>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
                  <span className="h-2 w-2 rounded-full bg-success" />
                  Live
                </span>
              </div>
              {recentActivity.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-ink-muted">No recent activity.</p>
              ) : (
                <ul className="max-h-[22rem] divide-y divide-line overflow-y-auto">
                  {recentActivity.slice(0, 20).map((activity, index) => (
                    <ActivityRow
                      key={`${activity.returnId}-${index}`}
                      activity={activity}
                      onClick={() => navigate(paths.returnDetail(activity.returnId))}
                    />
                  ))}
                </ul>
              )}
            </Card>

            <div className="flex flex-col gap-4">
              <div className="rounded-xl bg-gradient-to-br from-brand to-brand-hover p-5 text-white shadow-card">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/80">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-white/15">
                    <LightningIcon />
                  </span>
                  Auto-Pilot
                </div>
                <div className="mt-3 text-4xl font-bold">{percentText(autopilot?.automatedRatePct)}</div>
                <p className="mt-1 text-sm text-white/80">of returns auto-processed in the last {days} days</p>

                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-white/15 pt-4">
                  <div>
                    <div className="text-2xl font-bold">{formatNumber(autopilot?.activeRules)}</div>
                    <div className="text-xs text-white/70">active rules</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{formatNumber(autopilot?.totalRuns30d)}</div>
                    <div className="text-xs text-white/70">runs · last 30d</div>
                  </div>
                </div>

                {topRules.length > 0 ? (
                  <ul className="mt-4 flex flex-col gap-2 border-t border-white/15 pt-4">
                    {topRules.slice(0, 4).map((rule) => (
                      <li key={rule.ruleId} className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate text-white/90">{rule.ruleName}</span>
                        <span className="shrink-0 font-mono text-xs text-white/70">
                          {formatNumber(rule.runsCount)} runs
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                <button
                  type="button"
                  onClick={() => navigate(paths.autopilot)}
                  className="mt-4 w-full rounded-lg bg-white/15 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/25"
                >
                  Open Auto-Pilot
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Presentational pieces ────────────────────────────────────────────────────

function Sparkline({ points, color }: { points: number[]; color: string }) {
  if (points.length < 2) {
    return null;
  }
  const width = 120;
  const height = 36;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const coords = points.map((value, index) => {
    const xCoord = index * step;
    const yCoord = height - 2 - ((value - min) / range) * (height - 6);
    return `${index === 0 ? 'M' : 'L'} ${xCoord.toFixed(1)} ${yCoord.toFixed(1)}`;
  });
  const line = coords.join(' ');
  const area = `${line} L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-9 w-full" preserveAspectRatio="none" aria-hidden="true">
      <path d={area} fill={color} fillOpacity="0.12" />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

type MetricTone = 'brand' | 'success' | 'info' | 'warning' | 'danger';

/** Soft circular icon-chip classes for the headline KPI cards. */
const METRIC_TONES: Record<MetricTone, string> = {
  brand: 'bg-brand-soft text-brand',
  success: 'bg-success-soft text-success',
  info: 'bg-info-soft text-info',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
};

function MetricCard({
  label,
  value,
  hint,
  delta,
  spark,
  accent,
  icon,
  tone = 'brand',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  delta?: MetricDelta | null;
  spark?: number[];
  accent?: string;
  icon?: ReactNode;
  tone?: MetricTone;
}) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          {icon ? (
            <span
              className={cn(
                'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
                METRIC_TONES[tone],
              )}
            >
              {icon}
            </span>
          ) : null}
          <span className="truncate text-sm text-ink-muted">{label}</span>
        </div>
        {delta ? (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-2 py-1 text-xs font-semibold leading-none',
              delta.isPositive ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger',
            )}
          >
            {delta.up ? '↑' : '↓'} {delta.text}
          </span>
        ) : null}
      </div>
      <div className="mt-1 text-2xl font-bold text-ink">{value}</div>
      <div className="mt-2 flex items-end justify-between gap-3">
        {hint ? <span className="text-xs text-ink-subtle">{hint}</span> : <span />}
        {spark && spark.length > 1 ? (
          <div className="w-24 flex-shrink-0">
            <Sparkline points={spark} color={accent ?? '#6366f1'} />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function PipelineStat({
  label,
  value,
  tone,
  onClick,
}: {
  label: string;
  value: number | null | undefined;
  tone?: 'danger';
  onClick?: () => void;
}) {
  const count = toNumber(value);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={cn(
        'flex flex-col rounded-xl border border-line bg-surface px-4 py-3 text-left shadow-card transition-colors',
        onClick && 'hover:border-brand/40',
        !onClick && 'cursor-default',
      )}
    >
      <span className={cn('text-xl font-bold', tone === 'danger' && count > 0 ? 'text-danger' : 'text-ink')}>
        {formatNumber(count)}
      </span>
      <span className="mt-0.5 text-xs text-ink-muted">{label}</span>
    </button>
  );
}

function AttentionTile({
  icon,
  iconClass,
  label,
  value,
  onClick,
}: {
  icon: ReactNode;
  iconClass: string;
  label: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-4 rounded-xl border border-line bg-surface p-4 text-left shadow-card transition-colors hover:border-brand/40"
    >
      <span className={cn('flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl', iconClass)}>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-ink-muted">{label}</div>
        <div className="text-2xl font-bold text-ink">{value}</div>
      </div>
      <span className="text-ink-subtle">
        <ChevronIcon />
      </span>
    </button>
  );
}

/** Colour of the activity dot, keyed off the event outcome. */
function activityTone(activity: DashboardActivity): string {
  const event = activity.eventType ?? '';
  const status = activity.toStatus ?? '';
  if (event.includes('FAILED') || event.includes('ERROR') || status === 'REJECTED') {
    return 'bg-danger';
  }
  if (status === 'REFUNDED' || status.includes('APPROVED') || event.includes('REFUND')) {
    return 'bg-success';
  }
  if (event === 'RETURN_SUBMITTED') {
    return 'bg-brand';
  }
  return 'bg-info';
}

function actorLabel(activity: DashboardActivity): string {
  if (activity.actorType === 'AUTOPILOT') {
    return 'Auto-Pilot';
  }
  return humanizeEnum(activity.actorType) || 'System';
}

function ActivityRow({ activity, onClick }: { activity: DashboardActivity; onClick: () => void }) {
  return (
    <li
      className="flex cursor-pointer items-start gap-3 px-5 py-3 transition-colors hover:bg-surface-sunken/50"
      onClick={onClick}
    >
      <span className={cn('mt-1.5 h-2 w-2 flex-shrink-0 rounded-full', activityTone(activity))} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ink">
          <span className="font-semibold">{actorLabel(activity)}</span> {humanizeEnum(activity.eventType).toLowerCase()}{' '}
          <span className="font-medium">{activity.returnId}</span>
        </p>
        {activity.note ? <p className="mt-0.5 line-clamp-2 text-xs text-ink-subtle">{activity.note}</p> : null}
      </div>
      <span className="whitespace-nowrap text-xs text-ink-subtle">{formatRelativeTime(activity.createdAt)}</span>
    </li>
  );
}

